import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { CATEGORIES } from "@/data/productCategories";

const ease = [0.19, 1, 0.22, 1];

/**
 * Build the flat product list for the Designs category — merges gallery
 * (studio) and lifestyleGallery (in-situ) and assigns codes ESP1, ESP2, ...
 */
function getDesignProducts() {
  const cat = CATEGORIES.designs;
  const prefix = cat.codePrefix || "ESP";
  const items = [...(cat.gallery || []), ...(cat.lifestyleGallery || [])];
  return items.map((item, i) => ({
    ...item,
    code: `${prefix}${i + 1}`,
    isStudio: i < (cat.gallery?.length || 0),
  }));
}

const SpecRow = ({ label, value }) =>
  value ? (
    <div
      className="grid grid-cols-[140px_1fr] gap-6 border-t border-[#0a0a0c]/10 py-4"
      data-testid={`spec-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <dt className="font-tech text-[10px] uppercase tracking-[0.28em] text-[#8a6a3d]">
        {label}
      </dt>
      <dd className="font-body text-[14px] leading-[1.7] text-[#0a0a0c]/80">
        {value}
      </dd>
    </div>
  ) : null;

export default function DesignDetail() {
  const { modelId } = useParams();
  const products = getDesignProducts();
  const idx = products.findIndex(
    (p) => p.code.toLowerCase() === (modelId || "").toLowerCase()
  );
  if (idx === -1) return <Navigate to="/products/designs" replace />;

  const p = products[idx];
  const prev = products[(idx - 1 + products.length) % products.length];
  const next = products[(idx + 1) % products.length];
  const related = products.filter((r) => r.code !== p.code).slice(0, 3);
  const typeLabel = {
    door: "Door",
    window: "Window",
    screen: "Screen",
  }[p.type || "door"];

  return (
    <div
      data-testid={`design-detail-${p.code.toLowerCase()}`}
      className="min-h-screen bg-white text-[#0a0a0c]"
    >
      {/* Breadcrumb + prev/next */}
      <section className="mx-auto max-w-[1400px] px-6 pb-6 pt-32 lg:px-16 lg:pt-40">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#0a0a0c]/10 pb-4">
          <nav className="font-tech text-[10px] uppercase tracking-[0.32em] text-[#0a0a0c]/50">
            <Link to="/products" className="hover:text-[#c6a87c]">
              Products
            </Link>
            <span className="mx-2">/</span>
            <Link to="/products/designs" className="hover:text-[#c6a87c]">
              Designs
            </Link>
            <span className="mx-2">/</span>
            <span className="text-[#0a0a0c]">Model {p.code}</span>
          </nav>
          <div className="flex items-center gap-4 font-tech text-[10px] uppercase tracking-[0.32em]">
            <Link
              to={`/products/designs/${prev.code.toLowerCase()}`}
              data-testid="design-prev"
              className="text-[#0a0a0c]/60 hover:text-[#c6a87c]"
            >
              ← {prev.code}
            </Link>
            <Link
              to={`/products/designs/${next.code.toLowerCase()}`}
              data-testid="design-next"
              className="text-[#0a0a0c]/60 hover:text-[#c6a87c]"
            >
              {next.code} →
            </Link>
          </div>
        </div>
      </section>

      {/* Hero: image + spec panel */}
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 pb-24 pt-10 lg:grid-cols-[7fr_5fr] lg:gap-14 lg:px-16 lg:pb-32 lg:pt-14">
        {/* Left: hero image */}
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="relative"
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#f4f2ee]">
            <img
              src={p.src}
              alt={p.name}
              className={
                p.isStudio
                  ? "absolute inset-0 h-full w-full object-contain p-6 lg:p-10"
                  : "absolute inset-0 h-full w-full object-cover"
              }
            />
          </div>
          <figcaption className="mt-4 font-tech text-[10px] uppercase tracking-[0.32em] text-[#0a0a0c]/50">
            {p.isStudio ? "Studio render" : "Installed on-site"}
          </figcaption>
        </motion.figure>

        {/* Right: spec panel */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease }}
          className="flex flex-col"
        >
          <span
            data-testid="design-detail-type"
            className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#8a6a3d]"
          >
            — {typeLabel}
          </span>
          <h1
            data-testid="design-detail-code"
            className="mt-4 font-serif-display text-5xl font-bold uppercase leading-[0.98] tracking-tight text-[#0a0a0c] lg:text-6xl"
          >
            Model {p.code}
          </h1>
          <p className="mt-4 font-serif-display text-xl font-medium tracking-tight text-[#0a0a0c]/85 lg:text-2xl">
            {p.name}
          </p>
          <p className="mt-3 font-body text-[14px] leading-[1.7] text-[#0a0a0c]/60">
            {p.spec}
          </p>

          <dl className="mt-10">
            <SpecRow label="Type" value={typeLabel} />
            <SpecRow label="Profile" value={p.profile} />
            <SpecRow label="Dimensions" value={p.dimensions} />
            <SpecRow label="Glazing" value={p.glazing} />
            <SpecRow label="Finish" value={p.finish} />
            <div className="border-t border-[#0a0a0c]/10" />
          </dl>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="design-detail-enquire"
              className="rounded-full bg-[#0a0a0c] px-10 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-white transition-all duration-500 hover:bg-[#c6a87c]"
            >
              Enquire About {p.code}
            </Link>
            <Link
              to="/products/designs"
              data-testid="design-detail-back"
              className="rounded-full border border-[#0a0a0c]/25 px-10 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-[#0a0a0c]/80 transition-all duration-500 hover:border-[#c6a87c] hover:text-[#c6a87c]"
            >
              All Designs
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1400px] border-t border-[#0a0a0c]/10 px-6 py-20 lg:px-16 lg:py-24">
          <div className="mb-10 flex items-baseline justify-between">
            <h2 className="font-serif-display text-2xl font-bold uppercase tracking-tight text-[#0a0a0c] lg:text-3xl">
              Related Designs
            </h2>
            <Link
              to="/products/designs"
              className="font-tech text-[10px] uppercase tracking-[0.32em] text-[#0a0a0c]/60 hover:text-[#c6a87c]"
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:gap-10">
            {related.map((r) => (
              <Link
                key={r.code}
                to={`/products/designs/${r.code.toLowerCase()}`}
                data-testid={`related-${r.code.toLowerCase()}`}
                className="group"
              >
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#f4f2ee]">
                  <img
                    src={r.src}
                    alt={r.name}
                    loading="lazy"
                    className={
                      r.isStudio
                        ? "absolute inset-0 h-full w-full object-contain p-4 transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.03]"
                        : "absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.03]"
                    }
                  />
                </div>
                <p className="mt-3 text-center font-tech text-[11px] uppercase tracking-[0.32em] text-[#0a0a0c]">
                  Model: {r.code}
                </p>
                <p className="mt-1 text-center font-body text-[12px] text-[#0a0a0c]/55">
                  {r.name}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
